"use client"

import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { fetchProductsInterestAnalysis, fetchProductsDebtAnalysis } from "../../redux/actions/sales"
import jsPDF from "jspdf"
import "jspdf-autotable"
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"
import moment from "moment"
import { useTheme } from "@mui/material/styles"
import { tokens } from "../../theme"

const ProductSalesAnalysis = () => {
  const dispatch = useDispatch()
  const [selectedShop, setSelectedShop] = useState("")
  const [analysisType, setAnalysisType] = useState("interest")
  const [startDate, setStartDate] = useState(moment("2025-01-01"))
  const [endDate, setEndDate] = useState(moment())
  const [loading, setLoading] = useState(false)
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)

  const shops = useSelector((state) => state.shop.shops)
  const [analysisData, setAnalysisData] = useState([])

  useEffect(() => {
    if (selectedShop) {
      fetchAnalysisData()
    }
  }, [selectedShop, analysisType, startDate, endDate])

  const fetchAnalysisData = async () => {
    setLoading(true)
    try {
      if (analysisType === "interest") {
        const response = await dispatch(
          fetchProductsInterestAnalysis(selectedShop, {
            startDate: startDate.format("YYYY-MM-DD"),
            endDate: endDate.format("YYYY-MM-DD"),
          }),
        )
        setAnalysisData(response?.analysis || [])
      } else {
        const response = await dispatch(fetchProductsDebtAnalysis(selectedShop))
        setAnalysisData(response?.analysis || [])
      }
    } catch (error) {
      console.error("Erreur:", error)
      setAnalysisData([])
    } finally {
      setLoading(false)
    }
  }

  const calculateTotals = (data) => {
    return data.reduce(
      (acc, item) => ({
        totalQuantity: acc.totalQuantity + (item.totalQuantitySold || 0),
        totalAmount: acc.totalAmount + (item.totalAmount || 0),
        totalPriceAchat: acc.totalPriceAchat + (item.totalAmountPriceAchat || 0),
        totalInterest: acc.totalInterest + (item.interest || 0),
        totalAmountPaid: acc.totalAmountPaid + (item.totalAmountPaid || 0),
        totalRemaining: acc.totalRemaining + (item.totalRemainingAmount || 0),
        totalDebtCount: acc.totalDebtCount + (item.debtCount || 0),
      }),
      {
        totalQuantity: 0,
        totalAmount: 0,
        totalPriceAchat: 0,
        totalInterest: 0,
        totalAmountPaid: 0,
        totalRemaining: 0,
        totalDebtCount: 0,
      },
    )
  }

  const downloadPDF = () => {
    const doc = new jsPDF()

    const addHeader = (pageNumber) => {
      doc.setFont("helvetica", "bold")
      doc.setFontSize(16)
      const title = analysisType === "interest" ? "Analyse des Intérêts par Produit" : "Analyse des Dettes par Produit"
      doc.text(title, 14, 15)

      if (analysisType === "interest") {
        doc.setFont("helvetica", "normal")
        doc.setFontSize(10)
        doc.text(`Période: ${startDate.format("DD MMMM YYYY")} - ${endDate.format("DD MMMM YYYY")}`, 14, 25)
      }

      doc.setFontSize(8)
      doc.text(`Page ${pageNumber}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10)
    }

    const addFooter = () => {
      doc.setFontSize(8)
      doc.setTextColor(128)
      doc.text("Conçu par l'équipe Hyphen Tech", 14, doc.internal.pageSize.height - 10)
      doc.setTextColor(30, 58, 138)
      doc.textWithLink("www.hyphentech.tech", 14, doc.internal.pageSize.height - 6, {
        url: "https://www.hyphentech.tech/",
      })
    }

    const tableColumn =
      analysisType === "interest"
        ? ["Produit", "Quantité Vendue", "Montant Total", "Prix d'Achat Total", "Intérêt", "Pourcentage"]
        : ["Produit", "Quantité Vendue", "Montant Total", "Montant Payé", "Montant Restant", "Nombre de Dettes"]

    const tableRows = analysisData.map((item) => {
      if (analysisType === "interest") {
        return [
          item.productName,
          item.totalQuantitySold,
          `${item.totalAmount.toLocaleString()} F`,
          `${item.totalAmountPriceAchat.toLocaleString()} F`,
          `${item.interest.toLocaleString()} F`,
          `${item.interestPercentage.toFixed(2)}%`,
        ]
      } else {
        return [
          item.productName,
          item.totalQuantitySold,
          `${item.totalAmount.toLocaleString()} F`,
          `${item.totalAmountPaid.toLocaleString()} F`,
          `${item.totalRemainingAmount.toLocaleString()} F`,
          item.debtCount,
        ]
      }
    })

    const totals = calculateTotals(analysisData)

    const totalRow =
      analysisType === "interest"
        ? [
            "TOTAL",
            totals.totalQuantity,
            `${totals.totalAmount.toLocaleString()} F`,
            `${totals.totalPriceAchat.toLocaleString()} F`,
            `${totals.totalInterest.toLocaleString()} F`,
            `${((totals.totalInterest / totals.totalPriceAchat) * 100).toFixed(2)}%`,
          ]
        : [
            "TOTAL",
            totals.totalQuantity,
            `${totals.totalAmount.toLocaleString()} F`,
            `${totals.totalAmountPaid.toLocaleString()} F`,
            `${totals.totalRemaining.toLocaleString()} F`,
            totals.totalDebtCount,
          ]

    tableRows.push(totalRow)

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: analysisType === "interest" ? 30 : 20,
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 1 },
      headStyles: { fillColor: [30, 58, 138], textColor: 255 },
      footStyles: { fillColor: [240, 240, 240], textColor: 0, fontStyle: "bold" },
      didDrawPage: (data) => {
        addHeader(doc.internal.getNumberOfPages())
        addFooter()
      },
      createdRow: (row, data, index) => {
        if (index === tableRows.length - 1) {
          row.cells.forEach((cell) => {
            cell.styles.fontStyle = "bold"
            cell.styles.fillColor = [240, 240, 240]
          })
        }
      },
    })

    doc.save(`analyse-${analysisType === "interest" ? "interets" : "dettes"}-${moment().format("DD-MM-YYYY")}.pdf`)
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          color: "primary.main",
          fontWeight: "bold",
          mb: 3,
        }}
      >
        Analyse des Ventes par Produit
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
          p: 2,
          bgcolor: "background.neutral",
          borderRadius: 2,
          border: `1px solid ${colors.primary[200]}`,
        }}
      >
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Boutique</InputLabel>
          <Select value={selectedShop} onChange={(e) => setSelectedShop(e.target.value)} label="Boutique">
            {shops.map((shop) => (
              <MenuItem key={shop._id} value={shop._id}>
                {shop.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Type d'Analyse</InputLabel>
          <Select value={analysisType} onChange={(e) => setAnalysisType(e.target.value)} label="Type d'Analyse">
            <MenuItem value="interest">Analyse des Intérêts</MenuItem>
            <MenuItem value="debt">Analyse des Dettes</MenuItem>
          </Select>
        </FormControl>

        {analysisType === "interest" && (
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label="Date de début"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{ textField: { size: "small" } }}
            />
            <DatePicker
              label="Date de fin"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{ textField: { size: "small" } }}
            />
          </LocalizationProvider>
        )}

        <Button
          variant="contained"
          onClick={downloadPDF}
          disabled={!analysisData.length}
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            height: 40,
            fontWeight: "bold",
          }}
        >
          Télécharger PDF
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            border: `1px solid ${colors.primary[200]}`,
            boxShadow: `0 4px 12px rgba(${colors.primary[900]}, 0.08)`,
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  background: theme.palette.mode === "dark" ? colors.primary[900] : colors.primary[100],
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    color: theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[900],
                  }}
                >
                  Produit
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: "bold",
                    color: theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[900],
                  }}
                >
                  Quantité Vendue
                </TableCell>
                {analysisType === "interest" ? (
                  <>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        color: theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[900],
                      }}
                    >
                      Montant Total
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        color: theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[900],
                      }}
                    >
                      Prix d'Achat Total
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        color: theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[900],
                      }}
                    >
                      Intérêt
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        color: theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[900],
                      }}
                    >
                      Pourcentage
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        color: theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[900],
                      }}
                    >
                      Montant Total
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        color: theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[900],
                      }}
                    >
                      Montant Payé
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        color: theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[900],
                      }}
                    >
                      Montant Restant
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        color: theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[900],
                      }}
                    >
                      Nombre de Dettes
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {analysisData && analysisData.length > 0 ? (
                analysisData.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:hover": {
                        backgroundColor: theme.palette.mode === "dark" ? colors.grey[800] : colors.primary[50],
                      },
                    }}
                  >
                    <TableCell>{item?.productName || "N/A"}</TableCell>
                    <TableCell align="right">{item?.totalQuantitySold || 0}</TableCell>
                    {analysisType === "interest" ? (
                      <>
                        <TableCell align="right">{(item?.totalAmount || 0).toLocaleString()} F</TableCell>
                        <TableCell align="right">{(item?.totalAmountPriceAchat || 0).toLocaleString()} F</TableCell>
                        <TableCell align="right">{(item?.interest || 0).toLocaleString()} F</TableCell>
                        <TableCell align="right">{(item?.interestPercentage || 0).toFixed(2)}%</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell align="right">{(item?.totalAmount || 0).toLocaleString()} F</TableCell>
                        <TableCell align="right">{(item?.totalAmountPaid || 0).toLocaleString()} F</TableCell>
                        <TableCell align="right">{(item?.totalRemainingAmount || 0).toLocaleString()} F</TableCell>
                        <TableCell align="right">{item?.debtCount || 0}</TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={analysisType === "interest" ? 6 : 6} align="center">
                    Aucune donnée disponible
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && analysisData.length > 0 && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: theme.palette.mode === "dark" ? colors.grey[800] : colors.primary[50],
            borderRadius: 2,
            border: `1px solid ${colors.primary[200]}`,
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[900], fontWeight: "bold" }}
          >
            Totaux
          </Typography>
          {analysisType === "interest" ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography sx={{ color: theme.palette.text.primary }}>
                  Quantité Totale: <strong>{calculateTotals(analysisData).totalQuantity}</strong>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={{ color: theme.palette.text.primary }}>
                  Montant Total: <strong>{calculateTotals(analysisData).totalAmount.toLocaleString()} F</strong>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={{ color: theme.palette.text.primary }}>
                  Intérêt Total: <strong>{calculateTotals(analysisData).totalInterest.toLocaleString()} F</strong>
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography sx={{ color: theme.palette.text.primary }}>
                  Quantité Totale: <strong>{calculateTotals(analysisData).totalQuantity}</strong>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={{ color: theme.palette.text.primary }}>
                  Dette Totale: <strong>{calculateTotals(analysisData).totalRemaining.toLocaleString()} F</strong>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={{ color: theme.palette.text.primary }}>
                  Nombre Total de Dettes: <strong>{calculateTotals(analysisData).totalDebtCount}</strong>
                </Typography>
              </Grid>
            </Grid>
          )}
        </Box>
      )}
    </Box>
  )
}

export default ProductSalesAnalysis
